<template>
  <div ref="circleEl" :style="style" class="circle-wrapper">
    <svg
      :width="svgW"
      :height="svgH"
      overflow="visible"
      :viewBox="`0 0 ${svgW} ${svgH}`"
      class="absolute top-0 left-0"
    >
      <!-- hlavny kruh -->
      <circle
        :cx="bleedPx + rPx"
        :cy="bleedPx + rPx"
        :r="rPx"
        fill="none"
        stroke="black"
        stroke-width="2"
      />
      <!-- bleed outline -->
      <circle
        v-if="bleedPx > 0"
        :cx="bleedPx + rPx"
        :cy="bleedPx + rPx"
        :r="rPx + bleedPx"
        fill="none"
        stroke="#F79009"
        stroke-width="2"
        stroke-dasharray="6,4"
      />
    </svg>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, defineExpose, defineProps, defineEmits, watchEffect, type CSSProperties } from 'vue'
import { useCanvasUnits } from '@/composables/useCanvasUnits.ts'
import {useDesignerStore} from "@/stores/designerStore.ts";

const props = defineProps<{
  width: number
  height: number
  positionX: number
  positionY: number
  zIndex?: number
  bleedOffset?: number
}>()

const emit = defineEmits([
  'update:svg',
  'update:width',
  'update:height',
  'update:positionX',
  'update:positionY',
])

const { mmToPx } = useCanvasUnits()
const designerStore = useDesignerStore()
const circleEl = ref<HTMLElement | null>(null)

const rPx = computed(() => mmToPx(Math.min(props.width, props.height) / 2, 'x'))
const bleedPx = computed(() => mmToPx(props.bleedOffset || 0, 'x'))
const svgW = computed(() => (rPx.value + bleedPx.value) * 2)
const svgH = computed(() => (rPx.value + bleedPx.value) * 2)

const style = computed<Partial<CSSProperties>>(() => {
    void useDesignerStore().scaleTick;
  return {
    position: 'absolute',
    left: `${mmToPx(props.positionX, 'x') - bleedPx.value}px`,
    top: `${mmToPx(props.positionY, 'y') - bleedPx.value}px`,
    width: `${svgW.value}px`,
    height: `${svgH.value}px`,
    pointerEvents: 'none',
    zIndex: `${props.zIndex ?? 10}`,
  }
}
)

function getSvg() {
  const w = props.width
  const h = props.height
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}mm" height="${h}mm" viewBox="0 0 ${w} ${h}">
    <ellipse cx="${w / 2}" cy="${h / 2}" rx="${w / 2}" ry="${h / 2}" fill="none" stroke="black" stroke-width="0.5"/>
  </svg>`
}

defineExpose({ circleEl, getSvg })

// 👇 vždy keď sa niečo zmení, centruj a pošli čerstvé SVG
watchEffect(() => {
  // keď práve hydratujeme zo servera, nič neprepočítavaj
  if (designerStore.hydrating) {
    emit('update:svg', getSvg())
    return
  }

  const MAX_SIZE = 300
  let newW = props.width
  let newH = props.height

  // Circle: width a height musia byť rovnaké (priemer)
  const diameter = Math.min(newW, newH, MAX_SIZE)
  if (diameter !== newW) emit('update:width', diameter)
  if (diameter !== newH) emit('update:height', diameter)

  // Centrovanie
  const canvasW = designerStore.canvasSizeInMm.width
  const canvasH = designerStore.canvasSizeInMm.height
  emit('update:positionX', +(canvasW / 2 - diameter / 2).toFixed(2))
  emit('update:positionY', +(canvasH / 2 - diameter / 2).toFixed(2))
  emit('update:svg', getSvg())
})
</script>

<style scoped>
.circle-wrapper {
  /* bleed aj hlavny kruh su vnutri SVG */
}
</style>
